import { DOM } from '../ui/DOM.js'
import { Selector } from '../ui/Selectors.js'
import Color from '../models/Color.js'

/**
 * SliderUIController
 *
 * Responsible for updating the visibility and state of the slider-related
 * UI panels based on the currently selected color.
 */
export default class SliderUIController {
    /**
     * Synchronize slider UI with the given color.
     * Decides which panels to show/hide and (potentially) could be extended
     * to also set slider values based on the color.
     *
     * @param {Color|null} color - The current color selected in the app.
     */
    static syncToColor(color) {
        console.log('syncToColor')

        // Compute configuration for sliders based on the color properties
        const cfg = SliderUIController.getSliderConfigForColor(color)

        // Luminosity panel visibility
        if (cfg.showLuminosity) DOM.show(Selector.sliderLuminosityPanel)
        else DOM.hide(Selector.sliderLuminosityPanel)

        // Rotation panel visibility (texture rotation etc.)
        if (cfg.showRotate) DOM.show(Selector.sliderRotatePanel)
        else DOM.hide(Selector.sliderRotatePanel)

        // Scale panel visibility (texture scale etc.)
        if (cfg.showScale) DOM.show(Selector.sliderScalePanel)
        else DOM.hide(Selector.sliderScalePanel)

        // Alternative wheel control visibility (e.g. magic-tool–related)
        if (cfg.showWheelAlt) DOM.show(Selector.sliderWheelAlt)
        else DOM.hide(Selector.sliderWheelAlt)

        if (!color) return

        const sliderOpacity = DOM.get(Selector.sliderOpacity)
        if (sliderOpacity) sliderOpacity.value = cfg.opacity

        const sliderRotate = DOM.get(Selector.sliderRotate)
        if (sliderRotate) sliderRotate.value = cfg.rotate

        const sliderScale = DOM.get(Selector.sliderScale)
        if (sliderScale) sliderScale.value = cfg.scale
    }

    /**
     * Hide all slider-related panels.
     * Useful when there is no active color or when resetting the UI.
     */
    static clear() {
        DOM.hide(Selector.sliderLuminosityPanel)
        DOM.hide(Selector.sliderRotatePanel)
        DOM.hide(Selector.sliderScalePanel)
        DOM.hide(Selector.sliderWheelAlt)
    }

    /**
     * Compute slider configuration flags and values for a given color.
     *
     * @param {Color|null} color - The color for which we compute UI config.
     * @returns {{
     *   showLuminosity: boolean,
     *   showRotate: boolean,
     *   showScale: boolean,
     *   showWheelAlt: boolean,
     *   opacity: number,
     *   rotate: number,
     *   scale: number
     * }}
     */
    static getSliderConfigForColor(color) {
        // If there is no color, disable everything and reset values
        if (!color) {
            return {
                showLuminosity: false,
                showRotate: false,
                showScale: false,
                showWheelAlt: false,
                opacity: 0,
                rotate: 0,
                scale: 0,
            }
        }

        // A "plain" color has type Color.TYPE.COLOR
        // Other types would be textures, patterns, etc.
        const isPlainColor = color.type === Color.TYPE.COLOR

        // For plain colors, we only show luminosity.
        // For non-plain colors, we also show rotation and scale sliders.
        return {
            // Luminosity slider is always relevant when a color is present
            showLuminosity: true,

            // Rotation is only meaningful for textured/complex colors
            showRotate: !isPlainColor,

            // Scale is also only meaningful for textured/complex colors
            showScale: !isPlainColor,

            // Your UX decision: wheel is related to a magic tool, not here
            showWheelAlt: false, // your UX says wheel is magic-tool–related

            // Opacity / bleaching factor of the color
            opacity: color.bleach_value ?? Color.DEFAULT_BLEACH_VALUE,

            // Texture rotation: retain current value even if hidden
            rotate: color.texture_rotation ?? Color.DEFAULT_TEXTURE_ROTATION,

            // Texture scale: retain current value even if hidden
            scale: color.texture_scale ?? Color.DEFAULT_TEXTURE_SCALE,
        }
    }
}
